/**
 * 사용 가이드 목록 — 앱이 보여 주는 매뉴얼 전부와 읽기 문턱.
 *
 * 🔑 제목·부제·상태는 md 와 같아야 한다 — `utils/manual/__tests__/manual-catalog.test.ts` 가
 *   `artifacts/manual/*.md` 와 spec 「부제」를 읽어 대조한다(어긋나면 CI 가 빨갛다).
 * 🔑 차례는 홈의 차례다 — 같은 묶음 안에서 이 순서로 선다.
 */

import type { PlanId } from 'src/types/plan/plan-domain';
import type { ManualId, ManualEntry, ManualPurpose, ManualMenuGroup } from 'src/types/manual/manual-domain';

import { paths } from 'src/routes/paths';

/**
 * 가이드를 읽을 수 있는 가장 낮은 플랜 — 이 등급 이상이면 연다(`utils/plan/plan-tier` 의 차례).
 *
 * 「일부러」 `null` 이다 — 지금은 로그인한 사람 누구나 읽는다. 플랜 하나를 넣으면 화면 관문·API 관문·
 * 사이드바가 코드 수정 없이 함께 그 문턱으로 바뀐다(만료·플랜 불명은 그때부터 막힌다).
 */
export const GUIDE_MIN_PLAN: PlanId | null = null;

export const MANUAL_CATALOG: readonly ManualEntry[] = [
  {
    id: 'search-all',
    title: '컨텐츠 허브',
    subtitle: '키워드 하나로 지금 도는 광고·SNS 게시물·판매몰 상품을 한 화면에서 찾아보고 비교할 수 있어요',
    menuGroup: 'Discover',
    screenPath: paths.service.searchAll,
    menuIcon: 'menu-contenthub',
    status: 'draft',
  },
  {
    id: 'brand-hub',
    title: '브랜드 허브',
    subtitle: '브랜드 하나를 모든 각도에서 볼 수 있어요',
    menuGroup: 'Discover',
    screenPath: paths.service.brandHub,
    menuIcon: 'menu-brandhub',
    status: 'draft',
  },
  {
    id: 'creator-hub',
    title: '크리에이터 허브',
    subtitle: '채널 × 계정 하나를 모든 각도에서 볼 수 있어요',
    menuGroup: 'Discover',
    screenPath: paths.service.creators,
    menuIcon: 'menu-creatorhub',
    status: 'draft',
  },
  // 🔑 Research 네 갈래는 갈래마다 한 편이다 — 주소는 그 갈래의 주소(`/service/research/<갈래>`)
  {
    id: 'search',
    title: '소셜 게시물',
    subtitle: '궁금한 키워드를 지금 바로 소셜에서 찾아볼 수 있어요',
    menuGroup: 'Research',
    screenPath: paths.service.searchAllAnalysisTab('social'),
    menuIcon: 'menu-deepsearch',
    status: 'draft',
  },
  {
    id: 'meta-ads',
    title: 'Meta 광고',
    subtitle: '다른 브랜드가 지금 돌리는 광고를 찾아 나란히 비교할 수 있어요',
    menuGroup: 'Research',
    screenPath: paths.service.searchAllAnalysisTab('ads'),
    menuIcon: 'photo-thumbnail',
    status: 'draft',
  },
  {
    id: 'market',
    title: '판매몰 상품',
    subtitle: '경쟁 상품을 찾아 담고 나란히 비교할 수 있어요',
    menuGroup: 'Research',
    screenPath: paths.service.searchAllAnalysisTab('products'),
    menuIcon: 'menu-mall',
    status: 'draft',
  },
  {
    id: 'event-calendar',
    title: '이벤트 캘린더',
    subtitle: '브랜드가 언제 무슨 캠페인을 돌렸는지 한눈에 볼 수 있어요',
    menuGroup: 'Research',
    screenPath: paths.service.searchAllAnalysisTab('events'),
    menuIcon: 'calendar',
    status: 'draft',
  },
  {
    id: 'bookmark',
    title: '북마크',
    subtitle: '마음에 든 글·광고·상품을 폴더에 모아 두고 다시 볼 수 있어요',
    menuGroup: 'Discover',
    screenPath: paths.service.bookmark.root,
    menuIcon: 'menu-bookmark',
    status: 'draft',
  },
  {
    // 추적 관리가 사이드바 TRACKING 맨 위다 — 등록·알림·연동을 쥐고, 추적 화면 매뉴얼들은 이 편을 가리킨다
    id: 'status',
    title: '추적 관리',
    subtitle: '지금 무엇을 등록해 모으는 중인지 한 장에서 볼 수 있어요',
    menuGroup: 'Tracking',
    screenPath: paths.service.status,
    menuIcon: 'menu-status',
    status: 'draft',
  },
  {
    id: 'brand-mention',
    title: '브랜드 멘션 추적',
    subtitle: '우리 브랜드 이야기가 어디서 어떻게 오가는지 볼 수 있어요',
    menuGroup: 'Tracking',
    screenPath: paths.service.brand,
    menuIcon: 'menu-brandmention',
    status: 'draft',
  },
  {
    id: 'keyword-monitoring',
    title: '타겟 멘션 추적',
    subtitle: '경쟁사와 시장 키워드를 자동으로 지켜볼 수 있어요',
    menuGroup: 'Tracking',
    screenPath: paths.service.keywordMonitoring,
    menuIcon: 'menu-keywordmonitoring',
    status: 'draft',
  },
  {
    id: 'brand-account',
    title: '자사 계정 관리',
    subtitle: '우리가 올린 글의 성과를 보고 경쟁 브랜드와 견줄 수 있어요',
    menuGroup: 'Operations',
    screenPath: paths.service.account,
    menuIcon: 'menu-brandaccount',
    status: 'draft',
  },
  {
    id: 'influencer-check',
    title: '시딩 전 체크',
    subtitle: '제품을 보내기 전에 협업 후보를 한 번에 점검할 수 있어요',
    menuGroup: 'Operations',
    screenPath: paths.service.influencerCheck,
    menuIcon: 'menu-seedingcheck',
    status: 'draft',
  },
  {
    id: 'campaign',
    title: '시딩 성과 모니터링',
    subtitle: '제품을 보낸 뒤 올라온 포스팅을 자동으로 모아 캠페인별 성과를 비교할 수 있어요',
    menuGroup: 'Operations',
    screenPath: paths.service.campaign.root,
    menuIcon: 'menu-influencermonitoring',
    status: 'draft',
  },
  {
    id: 'data-stats',
    title: '심화분석',
    subtitle: '여러 조건을 나란히 놓고 수치를 견줄 수 있어요',
    menuGroup: 'Labs',
    screenPath: paths.service.dataStats,
    menuIcon: 'menu-labs',
    status: 'draft',
  },
  {
    id: 'widget',
    title: '위젯 설정',
    subtitle: '우리 브랜드 이야기를 자사몰에 띄울 위젯을 만들 수 있어요',
    menuGroup: 'Labs',
    screenPath: paths.service.externalDashboard,
    menuIcon: 'menu-widget',
    status: 'draft',
  },
  {
    // 설정은 사이드바 묶음이 아니라 계정 메뉴에서 연다 — 그래서 `Account` 묶음이다
    id: 'settings',
    title: '설정',
    subtitle: '워크스페이스 전체를 한 장에서 정할 수 있어요',
    menuGroup: 'Account',
    screenPath: paths.service.settings,
    menuIcon: 'menu-setting',
    status: 'draft',
  },
  {
    // 화면은 설정 「연결」의 MCP 구역 — 대화는 AI 도구 안에서 한다
    id: 'mcp',
    title: 'AI 도구 연결(MCP)',
    subtitle: 'Claude 같은 AI 도구에서 우리 데이터를 대화로 물어볼 수 있어요',
    menuGroup: 'Account',
    screenPath: paths.service.settingsTab('connect'),
    menuIcon: 'menu-chat',
    status: 'draft',
  },
];

/** 홈이 묶음을 세우는 차례와 묶음 머리 글자 — 사이드바 묶음은 subheader 와 같은 글자를 쓴다 */
export const MANUAL_MENU_GROUPS: readonly { group: ManualMenuGroup; label: string }[] = [
  { group: 'Discover', label: 'Discover' },
  { group: 'Research', label: 'Research' },
  { group: 'Tracking', label: 'Tracking' },
  { group: 'Operations', label: 'Operations' },
  { group: 'Labs', label: 'Labs' },
  { group: 'Account', label: '계정·설정' },
];

/** 길잡이 「그 밖의 기능」 묶음 맨 위에 세우는 매뉴얼 — 어느 목적에도 안 든 것만 효력이 있다 */
export const MANUAL_REST_LEAD: readonly ManualId[] = ['mcp'];

/**
 * 가이드 홈 맨 위 「무엇을 하고 싶으세요?」 — 화면 이름이 아니라 «하고 싶은 일»로 매뉴얼을 고르게 한다.
 * 카드 차례가 홈의 차례다. 칩은 `manuals` 차례로 서고, 못 읽는 매뉴얼도 칩은 선다(막는 일은 매뉴얼 화면).
 */
export const MANUAL_PURPOSES: readonly ManualPurpose[] = [
  {
    key: 'competitor-content',
    emoji: '🔍',
    icon: 'fluent-emoji:magnifying-glass-tilted-left',
    title: '경쟁사 광고·콘텐츠를 찾고 싶어요',
    shortTitle: '경쟁사 광고·콘텐츠 찾기',
    description: '다른 브랜드의 인기 콘텐츠와 광고를 한 화면에서 찾아보고 비교할 수 있어요.',
    manuals: ['search-all', 'meta-ads', 'brand-hub'],
  },
  {
    key: 'find-creator',
    emoji: '🤝',
    icon: 'fluent-emoji:handshake',
    title: '반응 좋은 인플루언서를 찾고 싶어요',
    shortTitle: '반응 좋은 인플루언서 찾기',
    description: '실제 반응이 좋았던 게시물을 기준으로 우리 브랜드에 맞는 인플루언서를 찾아보세요.',
    manuals: ['creator-hub', 'influencer-check'],
  },
  {
    key: 'brand-mention',
    emoji: '💬',
    icon: 'fluent-emoji:speech-balloon',
    title: '우리 브랜드 언급을 확인하고 싶어요',
    shortTitle: '우리 브랜드 언급 확인하기',
    description: '우리 브랜드가 어디서 어떻게 이야기되는지, 부정 언급까지 한눈에 확인해요.',
    manuals: ['brand-mention', 'keyword-monitoring', 'status'],
  },
  {
    key: 'seeding',
    emoji: '📦',
    icon: 'fluent-emoji:package',
    title: '시딩한 콘텐츠와 성과를 관리하고 싶어요',
    shortTitle: '시딩 캠페인 관리하기',
    description: '보낸 제품의 업로드 여부와 반응을 캠페인별로 모아 성과를 비교해요.',
    manuals: ['campaign', 'influencer-check'],
  },
  {
    key: 'track-keyword',
    emoji: '📈',
    icon: 'fluent-emoji:bar-chart',
    title: '특정 키워드를 계속 추적하고 싶어요',
    shortTitle: '키워드 추적하기',
    description: '관심 키워드의 언급량과 인기 콘텐츠를 꾸준히 지켜볼 수 있어요.',
    manuals: ['keyword-monitoring', 'status', 'search'],
  },
  {
    key: 'own-account',
    emoji: '⚙️',
    icon: 'fluent-emoji:gear',
    title: '우리 계정·위젯을 관리하고 싶어요',
    shortTitle: '계정·위젯 관리하기',
    description: '자사 계정 연결, 위젯 설정 등 계정과 관련된 기능을 확인해 보세요.',
    manuals: ['brand-account', 'widget', 'settings'],
  },
];

/**
 * 매뉴얼 그림·영상 파일 확장자 → 응답 Content-Type.
 * 영상(`mp4`·`webm`)은 본문에서 소리 없이 반복 재생되는 짧은 움직이는 컷이다.
 */
export const MANUAL_IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  mp4: 'video/mp4',
  webm: 'video/webm',
};

/** 목록에 실린 id 전부 — 서버가 요청 id 를 이 집합으로 거른다 */
export const MANUAL_IDS: readonly ManualId[] = MANUAL_CATALOG.map((entry) => entry.id);
